import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { X } from "lucide-react";

import { getUnassignedJobs, getTeamManagers, assignJob } from '../../../../../../services/staffingService';

const AssignJobsModal = ({ open, onClose, unassignedJobsCount, providerCompanyId }) => {
  const [unassignedJobs, setUnassignedJobs] = useState([]);
  const [dispatchManagers, setDispatchManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedManagers, setSelectedManagers] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState({});

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, providerCompanyId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was outside any dropdown container
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen({});
      }
    };

    if (Object.keys(dropdownOpen).some(key => dropdownOpen[key])) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsResp, managersResp] = await Promise.all([
        getUnassignedJobs(providerCompanyId),
        getTeamManagers(providerCompanyId)
      ]);
      
      setUnassignedJobs(jobsResp.data.unassignedJobs || []);
      
      const formattedManagers = managersResp.data.providerManagers
        .filter(item => item.user_id !== null) // Filter out managers with null user_id
        .map(item => ({
          label: `${item.user_id.firstName} ${item.user_id.lastName}`,
          value: item._id
        }));
      
      setDispatchManagers(formattedManagers);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleManagerSelect = (jobId, managerId) => {
    setSelectedManagers({
      ...selectedManagers,
      [jobId]: managerId
    });
    setDropdownOpen({
      ...dropdownOpen,
      [jobId]: false
    });
  };

  const toggleDropdown = (jobId) => {
    setDropdownOpen(prev => {
      const newState = {
        ...prev,
        [jobId]: !prev[jobId]
      };
      return newState;
    });
  };

  const handleAssignJob = async (jobId) => {
    const managerId = selectedManagers[jobId];
    if (!managerId) return;
    
    try {
      await assignJob(providerCompanyId, jobId, managerId);
      toast.success("Job assigned successfully");
      
      // Remove the assigned job from the list
      setUnassignedJobs(unassignedJobs.filter(job => job._id !== jobId));
      
      // If no more jobs left, close the modal
      if (unassignedJobs.length <= 1) {
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to update the main page
        }, 1500);
      }
    } catch (error) {
      console.error("Error assigning job:", error);
      toast.error("Failed to assign job");
    }
  };

  const getFirstShiftDate = (shifts) => {
    if (!shifts || shifts.length === 0) return "N/A";
    const sortedShifts = [...shifts].sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    );
    return moment(sortedShifts[0].startTime).format("MMM D, YYYY");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] backdrop-blur-sm font-inter">
      <div className="bg-white rounded-lg shadow-2xl w-[90%] max-w-[800px] h-[80vh] max-h-[800px] flex flex-col animate-in slide-in-from-bottom-5 duration-300 overflow-hidden">
        
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 m-0">
            Assign Dispatch Manager to New Job Posts ({unassignedJobs.length})
          </h3>
          <button 
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200" 
            onClick={onClose}
          >
            <X />
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 flex flex-col">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-600">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-sm">Loading unassigned jobs...</p>
            </div>
          ) : unassignedJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-600">
              <p className="text-sm">No unassigned jobs found</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">
              {unassignedJobs.map(job => (
                <div key={job._id} className="border border-gray-200 rounded-md bg-white shadow-sm">
                  <div className="p-4">
                    <div className="flex mb-2">
                      <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">
                        {job.isLead ? "Contact:" : "Customer:"}
                      </div>
                      <div className="flex-grow text-gray-900 font-medium">
                        {job.isLead 
                          ? job.contact?.name || "N/A"
                          : job.customer_company_id?.companyLegalName || "N/A"
                        }
                      </div>
                    </div>
                    
                    {job.isLead && job.contact?.email && (
                      <div className="flex mb-2">
                        <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">Email:</div>
                        <div className="flex-grow text-gray-700">
                          {job.contact.email}
                        </div>
                      </div>
                    )}
                    
                    {job.isLead && job.contact?.phone && (
                      <div className="flex mb-2">
                        <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">Phone:</div>
                        <div className="flex-grow text-gray-700">
                          {job.contact.phone}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex mb-2">
                      <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">Service Type:</div>
                      <div className="flex-grow text-gray-700">
                        {job.service?.split('_')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                      </div>
                    </div>
                    
                    <div className="flex mb-4">
                      <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">First Shift:</div>
                      <div className="flex-grow text-gray-700">
                        {getFirstShiftDate(job.shifts)}
                      </div>
                    </div>
                    
                    <div className="flex mb-4">
                      <div className="font-medium text-gray-600 w-[120px] flex-shrink-0">Assign To:</div>
                      <div className="flex-grow relative">
                        <div className="relative z-10 dropdown-container">
                          <button
                            onClick={() => toggleDropdown(job._id)}
                            className="form-input w-full text-left flex items-center justify-between cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          >
                            <span className={`${selectedManagers[job._id] ? 'text-gray-900' : 'text-gray-500'}`}>
                              {selectedManagers[job._id] 
                                ? dispatchManagers.find(m => m.value === selectedManagers[job._id])?.label 
                                : "Select Manager"
                              }
                            </span>
                            <svg className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen[job._id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {dropdownOpen[job._id] && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-xl z-[100] max-h-48 overflow-y-auto">
                              {dispatchManagers.length > 0 ? (
                                dispatchManagers.map((manager) => (
                                  <button
                                    key={manager.value}
                                    onClick={() => handleManagerSelect(job._id, manager.value)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-900 border-none bg-transparent cursor-pointer transition-colors duration-150"
                                  >
                                    {manager.label}
                                  </button>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-sm text-gray-500">
                                  No managers available
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <button 
                        disabled={!selectedManagers[job._id]}
                        onClick={() => handleAssignJob(job._id)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                          selectedManagers[job._id]
                            ? 'bg-primary text-white hover:bg-opacity-90 shadow-sm hover:shadow-md cursor-pointer'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                        }`}
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Modal Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 gap-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignJobsModal;